import React, { useState, useEffect } from "react";
import {
  Avatar,
  Box,
  IconButton,
  Typography,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import { useTheme } from "@mui/material/styles";
import EditProfileDialog from "../pages/Profile/EditProfile";
import { getUserInfoFromToken } from "../utils/authUtils";
import ProtectedImage from "./ProtectedImage";
import { BRAND } from "./auth/authTheme";

export default function AccountSettings({ userDetails, onLogout }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [userType, setUserType] = useState(null);

  const open = Boolean(anchorEl);

  const fullName = `${userDetails?.first_name || ""} ${userDetails?.last_name || ""}`.trim();
  const initials =
    `${userDetails?.first_name?.[0] || ""}${userDetails?.last_name?.[0] || ""}`.toUpperCase() ||
    "?";
  const roleLabel = userDetails?.employee_id || userType || "User";

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleClose();
    setShowDialog(true);
  };

  useEffect(() => {
    const { userType: type } = getUserInfoFromToken();
    setUserType(type);
  }, []);

  const avatarNode = (
    <Avatar
      sx={{
        width: 38,
        height: 38,
        bgcolor: BRAND.orange,
        fontSize: "0.875rem",
        fontWeight: 600,
      }}
    >
      {userDetails?.profile_data?.profile_image ? (
        <ProtectedImage
          path={userDetails.profile_data.profile_image}
          alt={fullName}
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />
      ) : (
        initials
      )}
    </Avatar>
  );

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label="Account settings"
        aria-controls={open ? "account-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        sx={{
          "&:hover .MuiAvatar-root": {
            boxShadow: `0 0 0 2px ${BRAND.orangeLight}`,
          },
        }}
      >
        {avatarNode}
      </IconButton>

      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.75,
              width: isMobile ? "min(280px, calc(100vw - 20px))" : 248,
              borderRadius: "12px",
              border: `1px solid ${BRAND.border}`,
              boxShadow: "0 12px 32px rgba(26, 43, 74, 0.14)",
              overflow: "hidden",
            },
          },
        }}
        sx={{
          zIndex: 1100,
          "& .MuiBackdrop-root": {
            bgcolor: isMobile ? "rgba(26, 43, 74, 0.45)" : "rgba(26, 43, 74, 0.08)",
          },
        }}
      >
        <Box sx={{ px: 1.5, pt: 1.25, pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            {userDetails?.profile_data?.profile_image ? (
              <ProtectedImage
                path={userDetails.profile_data.profile_image}
                alt={fullName}
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
            ) : (
              <Avatar sx={{ width: 44, height: 44, bgcolor: BRAND.orange, flexShrink: 0 }}>
                {initials}
              </Avatar>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "0.9375rem",
                  fontWeight: 700,
                  color: BRAND.navy,
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {fullName || "Account"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.8125rem",
                  color: BRAND.muted,
                  lineHeight: 1.3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {roleLabel}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: BRAND.border }} />

        <MenuItem onClick={handleProfileClick} sx={{ py: 1.1, mx: 0.75, borderRadius: "8px" }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <PersonOutlineIcon sx={{ fontSize: 20, color: BRAND.muted }} />
          </ListItemIcon>
          <ListItemText
            primary="Profile"
            primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem" }}
          />
        </MenuItem>

        <MenuItem onClick={handleClose} sx={{ py: 1.1, mx: 0.75, borderRadius: "8px" }}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <SettingsOutlinedIcon sx={{ fontSize: 20, color: BRAND.muted }} />
          </ListItemIcon>
          <ListItemText
            primary="Settings"
            primaryTypographyProps={{ fontSize: "0.875rem" }}
          />
        </MenuItem>

        {!isMobile && (
          <>
            <Divider sx={{ borderColor: BRAND.border, my: 0.5 }} />
            <MenuItem
              onClick={() => {
                handleClose();
                onLogout();
              }}
              sx={{
                py: 1.1,
                mx: 0.75,
                mb: 0.75,
                borderRadius: "8px",
                color: BRAND.orangeHover,
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <LogoutIcon sx={{ fontSize: 20, color: BRAND.orangeHover }} />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{ fontWeight: 600, fontSize: "0.875rem" }}
              />
            </MenuItem>
          </>
        )}
      </Menu>

      <EditProfileDialog open={showDialog} handleClose={() => setShowDialog(false)} />
    </>
  );
}
